import React from "react";
import Helmet from "react-helmet";
import {
  Card,
  CardMedia,
  CardTitle,
  CardText,
  CardActions,
  RaisedButton,
  FlatButton,
  CircularProgress,
  Dialog
} from "material-ui";
import Spacing from "material-ui/styles/spacing";
import { Link } from "react-router";
import moment from "moment-timezone";
import twix from "twix";
import marked from "marked";
import ReactPlayer from "react-player";
import PlayIcon from "material-ui/svg-icons/av/play-circle-outline";

import TagList from "./partials/tag-list.jsx";
import TicketButton from "./partials/ticket-button.jsx";
import SocialShareWidget from "./partials/social-share-widget.jsx";
import InjectedTenantHTML from "./partials/injected-tenant-html.jsx";
import FullPageBackground from "./partials/full-page-background.jsx";
import FilmCard from "./partials/film-card.jsx";
import VirtualScreeningIcon from "./partials/icons/virtual-screening-icon.jsx";

import * as util from "../util";

moment.updateLocale("en", {
  calendar: {
    lastDay: "[Yesterday at] LT",
    sameDay: "[Today at] LT",
    nextDay: "[Tomorrow at] LT",
    lastWeek: "[Last] dddd [at] LT",
    nextWeek: "dddd [at] LT",
    sameElse: "LL [at] LT"
  }
});

import connectToStores from "alt-utils/lib/connectToStores";
import EventStore from "../stores/EventStore";
import EventActions from "../actions/EventActions";
import FilmStore from "../stores/FilmStore";
import PersonStore from "../stores/PersonStore";
import PassStore from "../stores/PassStore";
import PassActions from "../actions/PassActions";
import AppUtil from "../utils/AppUtil.js";

@util.slugify(EventStore.getEventById, "eventId")
@connectToStores
class Event extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fetchedEvent: null,
      showTrailer: false,
      trailerError: ""
    };

    this.backToSchedule = this.backToSchedule.bind(this);
    this.getStyles = this.getStyles.bind(this);
    this.generateMetadata = this.generateMetadata.bind(this);
  }

  static getStores() {
    return [EventStore, FilmStore, PersonStore];
  }

  static getPropsFromStores() {
    return {
      getEventById: EventStore.getEventById,
      getFilmById: FilmStore.getFilmById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      isLarge: React.PropTypes.bool
    };
  }

  componentDidMount() {
    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(newProps) {
    let { eventId } = newProps.params;
    if (this.state.fetchedEvent !== eventId) {
      this.setState({ fetchedEvent: eventId });
      EventActions.fetchOne(eventId);
    }

    if (newProps.isLoggedIn) {
      PassActions.fetchAll.defer({ firstTimeOnly: true });
    }
  }

  backToSchedule() {
    if (
      this.props.location.state &&
      (this.props.location.state.fromSchedule || this.props.location.state.from)
    ) {
      this.context.router.goBack();
    } else {
      this.context.router.push("/schedule");
    }
  }

  getStyles() {
    return {
      card: {
        borderRadius: "0",
        overflow: "visible",
        position: "relative"
      },
      cardSmall: {
        boxShadow: "none"
      },
      cardSmallWithFooter: {
        borderBottom: "1px solid #ccc"
      },
      bannerPaddedCard: {
        marginTop: 10
      },
      backToSchedule: {
        position: "absolute",
        left: 16,
        top: 16,
        zIndex: 1
      },
      media: {},
      cardTitle: {
        padding: "10px 16px 16px"
      },
      cardTitleWithPlayTrailerButton: {
        paddingRight: 140
      },
      cardTitleWithPlayTrailerButtonLarge: {
        paddingRight: 180
      },
      subtitle: {
        color: "rgba(255, 255, 255, 0.85)",
        marginTop: 6,
        lineHeight: "17px"
      },
      playTrailerButton: {
        position: "absolute",
        right: 16,
        top: 10
      },
      playTrailerButtonUndated: {
        position: "absolute",
        right: 16,
        top: 4
      },
      smallCardTitle: {
        position: "relative"
      },
      smallMediaContainer: {
        position: "relative"
      },
      descriptionHtml: {
        wordWrap: "break-word",
        marginBottom: "0.5em"
      },
      descriptionWithFilms: {
        marginBottom: "1em"
      },
      directionsLink: {
        color: "#fff",
        textDecoration: "none"
      },
      cardText: {
        minHeight: 200,
        fontSize: 15,
        lineHeight: "22px",
        color: "rgb(62, 62, 62)"
      },
      actionButtons: {
        // padding: '16px'
      },
      h3: {
        margin: "10px 0"
      },
      venue: {
        clear: "both",
        marginTop: "1em"
      },
      socialShare: {
        marginTop: 20
      },
      cardFooter: {
        boxShadow:
          "0 1px 6px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.24)",
        padding: 30,
        margin: "0 20px",
        backgroundColor: "#fff"
      }
    };
  }

  generateMetadata(event) {

    if (!this.context.tenant.enable_structured_metadata) {
      return null;
    }

    let metadata = {};
    metadata["@context"] = "https://schema.org";
    metadata["@type"] = "Event";

    metadata.name = event.name;
    metadata.eventAttendanceMode = "https://schema.org/OfflineEventAttendanceMode";
    metadata.eventStatus = "https://schema.org/EventScheduled";
    metadata.description = event.description;
    
    let startTime = moment(event.start_time).tz(event.timezone);
    let endTime = moment(event.end_time).tz(event.timezone);

    metadata.startDate = startTime.format("YYYY-MM-DDTHHmm");
    metadata.endDate = endTime.format("YYYY-MM-DDTHHmm");

    metadata.location = {};
    metadata.location["@type"] = "Place";
    metadata.location.name = event.location;
        
    metadata.offers = [];

    for (let tb=0; tb < event.ticket_buckets.length; tb++) {
      const thisTicketBucket = event.ticket_buckets[tb];
      let offer = {};
      offer["@type"] = "Offer";
      offer.price = thisTicketBucket.price / 100;
      if (this.props.appSettings.class === "yearround") {
        offer.url = window.location.origin + "/schedule/tickets/" + event.id;
      }
      else {
        offer.url = window.location.origin + "/schedule/" + event.id + "/tickets";
      }
      offer.priceCurrency = "USD";
      if (event.tickets_available) {
        offer.availability = "https://schema.org/InStock";
      }
      else {
        offer.availability = "https://schema.org/SoldOut";
      }
      offer.validFrom = startTime.format("YYYY-MM-DDTHHmm");   
      metadata.offers.push(offer);
    }      

    return metadata;
  }

  render() {
    let event = this.props.getEventById(this.props.params.eventId),
      mapUrl =
        event &&
        event.venue.address &&
        `https://www.google.com/maps/dir//${encodeURIComponent(
          event.venue.address
        )}`;

    if (!event) {
      return (
        <div style={{ padding: 10, textAlign: "center" }}>
          <CircularProgress
            mode="indeterminate"
            size={119}
            thickness={7}
            style={{ margin: 10.5 }}
          />
        </div>
      );
    }

    if (event && event.event_bucket && this.context.tenant && this.context.tenant.event_bucket) {
      if (event.event_bucket !== this.context.tenant.event_bucket) {
        return (
          <div style={{ fontSize: "1.5em", padding: 10, paddingTop: 70, textAlign: "center" }}>
            Unknown event, please double-check the URL
          </div>
        );
      }
    }

    const structuredMetadata = this.generateMetadata(event);

    let backLabel =
      (this.props.location.state && this.props.location.state.from) ||
      this.context.tenant.display_schedule;

    let styles = this.getStyles(),
      when = moment(event.start_time).tz(event.timezone).format("LLLL z"),
      where = event.venue.name,
      ticketButton = (
        <TicketButton
          event={event}
          // onBackToSchedule: Timeout to help prevent phantom tap effects on mobile
          onBackToSchedule={
            this.context.isLarge
              ? this.backToSchedule
              : () => setTimeout(() => this.backToSchedule(), 150)
          }
          hideButton={event.hide_tickets_button}
          backLabel={backLabel}
        />
      );

    // If no background image, fallback to fill color
    let fallbackFillColor = util.getColorForTag(
      (this.context.tenant.schedule_dominant_tag === "venue" &&
      event.venue &&
      event.venue.color
        ? event.venue
        : null) || (event.tags.length ? event.tags[0] : event.name),
      false,
      false,
      this.context.tenant
    );

    // Generate fields based upon film if specified
    if (event.films.length) {
      event.images.cover = event.images.cover || event.films[0].cover_image;
      if (this.context.tenant.film_guide_stills_mode && !event.images.cover) {
        event.images.cover = event.films[0].still_image;
        event.images.cover_1080p = true;
      }
      //event.trailer_url = event.films[0].trailer_url;

      // no trailer
      delete event.trailer_url;
    }

    // only show tags that aren't on the films
    const eventTags = event.tags.filter(
      t =>
        t.type !== "film" &&
        !event.films.some(f => f.tags.some(filmTag => filmTag.id === t.id))
    );

    return (
      <div>
        <Helmet
          title={event.name}
          meta={[
            { name: "og:title", content: event.name },
            { name: "og:description", content: event.short_description },
            { name: "og:image", content: event.images.cover }
          ]}
          script={structuredMetadata ? 
            [{ 
              type: "application/ld+json", 
              innerHTML: JSON.stringify(structuredMetadata)
            }]
          : []} 
        />
        {this.props.children
          ? React.cloneElement(this.props.children, {
              appSettings: this.props.appSettings
            })
          : undefined}
        {this.context.tenant.show_event_full_page_background ? (
          <FullPageBackground src={event.images.cover} />
        ) : null}
        {event.trailer_url ? (
          <Dialog
            open={this.state.showTrailer}
            bodyStyle={{ padding: 0, backgroundColor: "#000" }}
            className="Eventive--Dialog Eventive--Dialog--FilmTrailer"
            onRequestClose={() => this.setState({ showTrailer: false })}
          >
            {this.state.trailerError ||
            !ReactPlayer.canPlay(event.trailer_url) ? (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  backgroundColor: "#fff"
                }}
              >
                Oh dear!{" "}
                {this.state.trailerError ||
                  "The trailer could not be embedded."}{" "}
                Try going to the{" "}
                <a href={event.trailer_url} target="_blank">
                  original video
                </a>
                .
              </div>
            ) : (
              <div className="ReactPlayer--container">
                <ReactPlayer
                  url={event.trailer_url}
                  playing={this.state.showTrailer}
                  controls={true}
                  onError={e => this.setState({ trailerError: e.message })}
                  onPlay={() => this.setState({ trailerError: "" })}
                />
              </div>
            )}
          </Dialog>
        ) : null}
        <Card
          style={Object.assign(
            {},
            styles.card,
            !this.context.isLarge && styles.cardSmall,
            !this.context.isLarge &&
              !!this.context.tenant.event_footer.__html &&
              styles.cardSmallWithFooter,
            this.context.isLarge &&
              this.context.tenant.add_event_banner_padding &&
              styles.bannerPaddedCard
          )}
        >
          {this.context.isLarge &&
          !AppUtil.isSundanceMobileMode(this.context.tenant) ? (
            <div style={styles.backToSchedule}>
              <RaisedButton
                label={
                  <span>
                    <span style={{ fontSize: "1.4em", verticalAlign: -1 }}>
                      ‹
                    </span>{" "}
                    {backLabel}
                  </span>
                }
                style={{ marginRight: 7 }}
                onClick={this.backToSchedule}
              />
            </div>
          ) : null}
          <CardMedia
            overlay={
              <CardTitle
                style={Object.assign(
                  {},
                  styles.cardTitle,
                  event.trailer_url &&
                    (this.context.isLarge
                      ? styles.cardTitleWithPlayTrailerButtonLarge
                      : styles.cardTitleWithPlayTrailerButton),
                  !this.context.isLarge && styles.smallCardTitle
                )}
                titleStyle={{ lineHeight: "25px" }}
                title={
                  event.is_virtual ? (
                    <div>
                      <VirtualScreeningIcon
                        styles={{
                          float: "left",
                          height: 27,
                          paddingRight: 10,
                          paddingTop: 1
                        }}
                        scale="1.1"
                      />
                      {event.name}
                    </div>
                  ) : (
                    event.name
                  )
                }
                subtitle={
                  event.is_dated ? (
                    <div style={styles.subtitle}>
                      {when}
                      {when && where ? <br /> : null}
                      {where}
                    </div>
                  ) : null
                }
              >
                {event.trailer_url ? (
                  <RaisedButton
                    label={this.context.isLarge ? "Play trailer" : "Trailer"}
                    icon={<PlayIcon style={{ verticalAlign: -7 }} />}
                    style={
                      event.is_dated
                        ? styles.playTrailerButton
                        : styles.playTrailerButtonUndated
                    }
                    labelStyle={{ verticalAlign: 0 }}
                    onClick={() =>
                      this.setState({ showTrailer: true, trailerError: "" })
                    }
                  />
                ) : null}
              </CardTitle>
            }
            overlayContainerStyle={
              !this.context.isLarge ? styles.smallMediaContainer : null
            }
            overlayContentStyle={
              !this.context.isLarge ? styles.smallMediaContainer : null
            }
            style={styles.media}
          >
            <div style={{ backgroundColor: "#ccc" }}>
              {event.images.cover ? (
                <div
                  style={{
                    paddingBottom: event.images.cover_1080p
                      ? "56.25%"
                      : "39.58333333%",
                    backgroundImage: `url(${event.images.cover})`,
                    backgroundPosition: "center",
                    backgroundSize: "cover"
                  }}
                />
              ) : (
                <div
                  style={{ minHeight: 176, backgroundColor: fallbackFillColor }}
                ></div>
              )}
            </div>
          </CardMedia>
          <CardText style={styles.cardText}>
            {ticketButton}
            {eventTags.length &&
            !AppUtil.isSundanceMobileMode(this.context.tenant) ? (
              <TagList
                style={{ marginBottom: "1em" }}
                tags={
                  eventTags
                } /* don't show film tags since they are shown below */
                tooltip={t => `View all events tagged "${t}"`}
                onClick={tag =>
                  this.context.router.push(
                    `/schedule?filterTags[${tag.id}]=true`
                  )
                }
              />
            ) : (
              <div />
            )}
            <InjectedTenantHTML
              style={Object.assign(
                styles.descriptionHtml,
                event.description &&
                  event.films.length &&
                  styles.descriptionWithFilms
              )}
              content={{ __html: marked(event.description || "") }}
            />
            {event.films
              .filter(f => f.visibility !== "hidden_everywhere")
              .map((f, i) => {
                let film = this.props.getFilmById(f.id);
                return (
                  <FilmCard
                    film={film}
                    currentEventId={event.id}
                    /*hideTitle={event.films.length === 1}*/ hidePoster={
                      event.films.length === 1
                    }
                    style={i > 0 ? { marginTop: "2em" } : {}}
                    eventLinkState={{ from: "event" }}
                    person={
                      PersonStore.getState().isLoggedIn &&
                      PersonStore.getPersonId()
                    }
                    passes={PassStore.getPassesForCurrentEventBucket()}
                  />
                );
              })}
            {event.venue.name ? (
              <div style={styles.venue}>
                <strong>{event.venue.name}</strong>
                {event.venue.description ? (
                  this.context.tenant.sundance_login ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: event.venue.description
                      }}
                    />
                  ) : (
                    <div>{event.venue.description}</div>
                  )
                ) : null}
                {event.venue.address ? (
                  <div>
                    {event.venue.address.split("\n").map((l, i) => (
                      <span key={i}>
                        {l}
                        <br />
                      </span>
                    ))}
                  </div>
                ) : null}
                <div>
                  {moment(event.start_time).twix(event.end_time).format({
                    monthFormat: "MMMM",
                    implicitDate: false,
                    implicitYear: false,
                    implicitMinutes: false
                  })}{" "}
                  {moment(event.start_time).tz(event.timezone).format("z")}
                </div>
                <div>
                  {event.venue.address && (
                    <span>
                      {" "}
                      <a href={mapUrl} target="_blank">
                        Get directions
                      </a>{" "}
                      {!AppUtil.isSundanceMobileMode(this.context.tenant) ? (
                        <span> &middot; </span>
                      ) : null}
                    </span>
                  )}

                  {!AppUtil.isSundanceMobileMode(this.context.tenant) ? (
                    <Link to={`/schedule?filterVenues[${event.venue.id}]=true`}>
                      More events at venue
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}
            {!AppUtil.isSundanceMobileMode(this.context.tenant) ? (
              <SocialShareWidget style={styles.socialShare} />
            ) : null}
          </CardText>
        </Card>
        {this.context.tenant.event_footer.__html ? (
          <InjectedTenantHTML
            style={styles.cardFooter}
            content={this.context.tenant.event_footer}
          />
        ) : null}
      </div>
    );
  }
}

export default Event;



// WEBPACK FOOTER //
// ./src/app/components/event.jsx